/**
 * OphthalmoOnboarding — Gamified onboarding for new ophthalmologist members.
 *
 * Tracks 4 steps:
 *  1. Profil personnel rempli
 *  2. Adhésion validée
 *  3. Première fiche praticien créée
 *  4. Découverte de l'écosystème (forum, publications, formations, replays)
 *
 * Displays:
 *  - A welcome modal on first ever login (until dismissed)
 *  - A persistent progress widget at the top of the dashboard until 100%
 *  - Confetti animation when a step is completed
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, X, Trophy, ChevronRight, CheckCircle2, Circle,
  User, IdCard, CreditCard, BookOpen, MessageSquare, Video, FileText,
  Rocket, ArrowRight, PartyPopper,
} from 'lucide-react';
import { API } from '@/App';

const STORAGE_KEY_DISMISSED = 'ophthalmo_onboarding_dismissed_v1';
const STORAGE_KEY_WELCOME_SHOWN = 'ophthalmo_onboarding_welcome_v1';
const STORAGE_KEY_TOUR_DONE = 'ophthalmo_onboarding_tour_done_v1';

const OphthalmoOnboarding = ({ user, onDismiss }) => {
  const navigate = useNavigate();
  const [ficheCount, setFicheCount] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY_DISMISSED) === '1'
  );

  // Show welcome modal only the very first time
  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY_WELCOME_SHOWN)) {
      setWelcomeOpen(true);
      localStorage.setItem(STORAGE_KEY_WELCOME_SHOWN, '1');
    }
  }, []);

  // Fetch user fiche count
  useEffect(() => {
    axios.get(`${API}/my-fiches`)
      .then(r => setFicheCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => setFicheCount(0));
  }, []);

  // Compute step completion
  const steps = useMemo(() => {
    const profileFilled = !!(user?.first_name && user?.last_name && user?.phone && user?.bio);
    const adhesionActive = user?.subscription_status === 'active' || user?.subscription_status === 'paid';
    const hasFiche = (ficheCount ?? 0) > 0;
    const tourDone = localStorage.getItem(STORAGE_KEY_TOUR_DONE) === '1';

    return [
      {
        key: 'profile',
        title: 'Complétez votre profil',
        subtitle: 'Photo, téléphone personnel, présentation',
        done: profileFilled,
        icon: User,
        color: '#35AFE4',
        bg: '#eaf6fc',
        cta: 'Compléter mon profil',
        path: '/mon-profil',
      },
      {
        key: 'adhesion',
        title: 'Validez votre adhésion',
        subtitle: 'Adhérez à la SFOALC via HelloAsso',
        done: adhesionActive,
        icon: CreditCard,
        color: '#16a34a',
        bg: '#dcfce7',
        cta: 'Adhérer maintenant',
        path: '/adhesion',
      },
      {
        key: 'fiche',
        title: 'Créez votre première fiche praticien',
        subtitle: 'Apparaissez dans l\'annuaire SFOALC',
        done: hasFiche,
        icon: IdCard,
        color: '#7c3aed',
        bg: '#ede9fe',
        cta: 'Créer ma fiche',
        path: '/mes-fiches',
      },
      {
        key: 'discover',
        title: "Découvrez l'écosystème SFOALC",
        subtitle: 'Forum, publications, formations, replays',
        done: tourDone,
        icon: Rocket,
        color: '#d97706',
        bg: '#fef3c7',
        cta: "Visiter en 2 min",
        action: () => { setTourStep(0); setTourOpen(true); },
      },
    ];
  }, [user, ficheCount]);

  const completedCount = steps.filter(s => s.done).length;
  const total = steps.length;
  const progress = Math.round((completedCount / total) * 100);
  const allDone = completedCount === total;

  // Confetti when reaching 100%
  useEffect(() => {
    if (allDone && !dismissed) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 4500);
      return () => clearTimeout(t);
    }
  }, [allDone, dismissed]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY_DISMISSED, '1');
    setDismissed(true);
    onDismiss?.();
  }, [onDismiss]);

  if (dismissed) return null;

  return (
    <>
      {/* === Welcome modal === */}
      {welcomeOpen && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" data-testid="onboarding-welcome">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 sm:p-10 relative shadow-2xl">
            <button onClick={() => setWelcomeOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              data-testid="welcome-close">
              <X size={16} className="text-gray-500" />
            </button>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center mx-auto mb-5 shadow-lg">
              <Sparkles size={28} className="text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#1e3a5f] mb-2"
              style={{ fontFamily: "'Barlow', sans-serif" }}>
              Bienvenue {user?.first_name} 👋
            </h2>
            <p className="text-center text-gray-500 text-sm mb-7 leading-relaxed">
              Quelques étapes simples pour profiter pleinement de votre espace SFOALC.
              Suivez la jauge de progression — vous allez débloquer le forum, les publications,
              les replays et bien plus.
            </p>
            <div className="bg-[#f5f9fb] rounded-2xl p-4 mb-6 space-y-2">
              {steps.map((s, i) => {
                const I = s.icon;
                return (
                  <div key={s.key} className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: s.bg, color: s.color }}>
                      <I size={15} />
                    </div>
                    <span className="font-bold text-gray-400 w-6">{i + 1}</span>
                    <span className="font-semibold text-[#1e3a5f]">{s.title}</span>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setWelcomeOpen(false)}
              className="w-full px-6 py-3 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white text-sm font-bold rounded-full transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              data-testid="welcome-start-btn">
              C'est parti <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* === Discovery tour modal === */}
      {tourOpen && (
        <DiscoveryTour
          step={tourStep}
          onPrev={() => setTourStep(s => Math.max(0, s - 1))}
          onNext={() => setTourStep(s => s + 1)}
          onClose={() => setTourOpen(false)}
          onFinish={() => {
            localStorage.setItem(STORAGE_KEY_TOUR_DONE, '1');
            setTourOpen(false);
            // trigger re-render
            window.dispatchEvent(new Event('storage'));
          }}
          navigate={navigate}
        />
      )}

      {/* === Persistent progress widget === */}
      <div
        className="relative rounded-2xl p-5 mb-6 overflow-hidden shadow-md"
        style={{
          background: allDone
            ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
            : 'linear-gradient(135deg, #1e3a5f 0%, #2d5f8a 50%, #35AFE4 100%)',
        }}
        data-testid="onboarding-widget"
      >
        {/* Confetti */}
        {celebrate && <Confetti />}

        <div className="flex items-start justify-between gap-3 relative z-10">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              {allDone ? <Trophy size={20} className="text-amber-300" /> : <Rocket size={20} className="text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/65 mb-0.5">
                {allDone ? '🎉 Profil 100% complet' : 'Démarrez votre parcours SFOALC'}
              </p>
              <h3 className="text-lg font-bold text-white truncate" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {allDone
                  ? "Bienvenue à bord !"
                  : `${completedCount} / ${total} étapes complétées`}
              </h3>
            </div>
          </div>
          <button onClick={handleDismiss}
            className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            title="Masquer"
            data-testid="onboarding-dismiss">
            <X size={13} className="text-white" />
          </button>
        </div>

        {/* === Jauge / Progress bar === */}
        <div className="relative z-10 mt-5">
          <div className="h-2.5 rounded-full bg-white/15 overflow-hidden" data-testid="onboarding-progress-bar">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progress}%`,
                background: allDone
                  ? 'linear-gradient(90deg, #fde047, #facc15, #fde047)'
                  : 'linear-gradient(90deg, #7dd3fc, #38bdf8, #fde047)',
                boxShadow: '0 0 14px rgba(253, 224, 71, 0.6)',
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-white/65 text-[11px]">Progression : {progress}%</span>
            {!allDone && <span className="text-white/65 text-[11px]">Plus que {total - completedCount} étape{total - completedCount > 1 ? 's' : ''}</span>}
          </div>
        </div>

        {/* Steps grid */}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const handleClick = () => {
              if (s.action) s.action();
              else if (s.path) navigate(s.path);
            };
            return (
              <button
                key={s.key}
                onClick={handleClick}
                className={`text-left bg-white/[0.08] hover:bg-white/[0.18] active:bg-white/[0.25] backdrop-blur rounded-xl p-3 border border-white/10 transition-all hover:scale-[1.02] group ${
                  s.done ? 'opacity-90' : ''
                }`}
                data-testid={`onboarding-step-${s.key}`}
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: s.done ? 'rgba(34,197,94,0.25)' : s.bg + '40' }}>
                    {s.done
                      ? <CheckCircle2 size={14} className="text-green-300" strokeWidth={2.5} />
                      : <Icon size={14} className="text-white" strokeWidth={2.2} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] uppercase tracking-wider text-white/55 font-bold">Étape {i + 1}</p>
                    <p className="text-[12px] font-bold text-white leading-tight">{s.title}</p>
                  </div>
                </div>
                <p className="text-[11px] text-white/65 leading-snug ml-9">{s.subtitle}</p>
                {!s.done && (
                  <p className="text-[11px] font-bold text-white mt-2 ml-9 flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    {s.cta} <ArrowRight size={11} />
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {allDone && (
          <p className="text-white/85 text-xs mt-4 text-center flex items-center justify-center gap-1.5 relative z-10">
            <PartyPopper size={14} className="text-amber-300" />
            Toutes vos fonctionnalités sont actives. Bonne navigation !
          </p>
        )}
      </div>
    </>
  );
};

// =====================================================================
// Confetti — Pure CSS particles
// =====================================================================
const Confetti = () => {
  const pieces = Array.from({ length: 30 });
  const colors = ['#fde047', '#7dd3fc', '#fda4af', '#86efac', '#c4b5fd'];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="onboarding-confetti">
      {pieces.map((_, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: '-10%',
            left: `${Math.random() * 100}%`,
            width: `${6 + Math.random() * 6}px`,
            height: `${10 + Math.random() * 10}px`,
            background: colors[i % colors.length],
            transform: `rotate(${Math.random() * 360}deg)`,
            animation: `confetti-fall ${2 + Math.random() * 2}s cubic-bezier(.16,.84,.42,1) ${Math.random() * 0.5}s forwards`,
            borderRadius: '2px',
            opacity: 0.85,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(450px) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// =====================================================================
// Discovery tour
// =====================================================================
const TOUR_STEPS = [
  {
    title: '📚 Publications & Articles scientifiques',
    desc: "Accédez à toutes les publications scientifiques de la SFOALC, indexées par année, sujet et auteurs. Téléchargez les rapports SFO en PDF.",
    cta: 'Voir les publications',
    path: '/publications',
    icon: FileText,
    color: '#35AFE4',
    bg: '#eaf6fc',
  },
  {
    title: '🎓 Formations en ligne',
    desc: "Cours, articles de formation et événements à venir. Filtrez par catégorie pour trouver ce qui vous intéresse.",
    cta: 'Voir les formations',
    path: '/nos-formations',
    icon: BookOpen,
    color: '#7c3aed',
    bg: '#ede9fe',
  },
  {
    title: '🎥 Replays vidéo',
    desc: "Revoyez les sessions des congrès et JRO précédents. Disponibles 24/7 pour les adhérents.",
    cta: 'Voir les replays',
    path: '/nos-replays',
    icon: Video,
    color: '#dc2626',
    bg: '#fee2e2',
  },
  {
    title: '💬 Forum privé entre adhérents',
    desc: "Échangez avec vos confrères ophtalmologistes adaptateurs. Posez vos questions, partagez vos cas, votez sur les sujets.",
    cta: 'Accéder au forum',
    path: '/forum',
    icon: MessageSquare,
    color: '#16a34a',
    bg: '#dcfce7',
  },
];

const DiscoveryTour = ({ step, onPrev, onNext, onClose, onFinish, navigate }) => {
  const total = TOUR_STEPS.length;
  const current = TOUR_STEPS[step];
  const Icon = current?.icon;
  const isLast = step === total - 1;

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[210] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" data-testid="discovery-tour">
      <div className="bg-white rounded-3xl max-w-md w-full p-7 sm:p-8 relative shadow-2xl">
        <button onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center" data-testid="tour-close">
          <X size={15} className="text-gray-500" />
        </button>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1.5 mb-5">
          {TOUR_STEPS.map((_, i) => (
            <div key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8' : 'w-1.5'}`}
              style={{ background: i <= step ? current.color : '#e5e7eb' }} />
          ))}
        </div>

        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-md"
          style={{ background: current.bg }}>
          <Icon size={28} style={{ color: current.color }} strokeWidth={2.2} />
        </div>

        <h3 className="text-xl font-bold text-center text-[#1e3a5f] mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
          {current.title}
        </h3>
        <p className="text-center text-gray-500 text-sm mb-6 leading-relaxed">{current.desc}</p>

        <button
          onClick={() => { onClose(); navigate(current.path); }}
          className="w-full px-5 py-2.5 mb-3 rounded-full text-white text-xs font-bold transition-all hover:shadow-md"
          style={{ background: current.color }}
          data-testid="tour-cta">
          {current.cta} →
        </button>

        <div className="flex items-center justify-between">
          <button onClick={onPrev} disabled={step === 0}
            className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30 px-2"
            data-testid="tour-prev">
            ← Précédent
          </button>
          <span className="text-xs text-gray-400">{step + 1} / {total}</span>
          {isLast ? (
            <button onClick={onFinish}
              className="text-xs font-bold text-[#16a34a] hover:text-[#15803d] flex items-center gap-1 px-2"
              data-testid="tour-finish">
              Terminer ✓
            </button>
          ) : (
            <button onClick={onNext}
              className="text-xs font-bold text-[#35AFE4] hover:text-[#1e9bd4] flex items-center gap-1 px-2"
              data-testid="tour-next">
              Suivant →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OphthalmoOnboarding;
